import { createContext, useContext, useState, useCallback, useMemo } from 'react';

const ShareContext = createContext(null);

/**
 * Lets any card anywhere in the tree open the share sheet without the handler
 * being threaded through the panel/grid/card prop chain.
 *
 * Request shape:
 *   { sourceType: 'selection' | 'closet' | 'lookbook' | 'vto_result',
 *     closetId?, lookbookId?, clothesIds?, vtoResultIds?, title?, previewImages? }
 */
export const ShareProvider = ({ children }) => {
  const [shareRequest, setShareRequest] = useState(null);

  const openShare = useCallback((request) => {
    if (!request?.sourceType) {
      console.warn('[Share] openShare called without a sourceType');
      return;
    }
    setShareRequest(request);
  }, []);

  const closeShare = useCallback(() => setShareRequest(null), []);

  const value = useMemo(
    () => ({ shareRequest, openShare, closeShare }),
    [shareRequest, openShare, closeShare]
  );

  return <ShareContext.Provider value={value}>{children}</ShareContext.Provider>;
};

/**
 * @returns {{shareRequest: object|null, openShare: Function, closeShare: Function}}
 */
export const useShare = () => {
  const ctx = useContext(ShareContext);
  if (!ctx) {
    // Rendering outside the provider shouldn't crash a card — no-op instead
    return { shareRequest: null, openShare: () => {}, closeShare: () => {} };
  }
  return ctx;
};

export default ShareContext;
